import { Capacitor } from '@capacitor/core'

const PREVIEW_KEY = 'vitesse_platform_preview'

export type AppearancePlatform = 'ios' | 'android' | 'web'

const APPEARANCE_PLATFORMS = new Set<string>(['ios', 'android', 'web'])

/**
 * Narrow an arbitrary string to an appearance. `Capacitor.getPlatform()` is typed
 * `string` and can answer with something outside this union (`electron`), and a
 * `data-platform` attribute read back off the DOM can be anything at all — so
 * both are checked rather than asserted.
 */
export function isAppearancePlatform(value: string | undefined | null): value is AppearancePlatform {
  return value != null && APPEARANCE_PLATFORMS.has(value)
}

/**
 * ⚠ Two different questions live here, and conflating them is a security-shaped
 * bug rather than a cosmetic one:
 *
 *   CAPABILITY — "am I actually running on a phone?" Drives secure storage, push
 *   registration, the HTTP layer, the router's history mode and the
 *   `platform-native` class that locks body scrolling. NEVER overridable.
 *
 *   APPEARANCE — "whose design language do I wear?" Drives `data-platform` and
 *   therefore the fonts, radii and bar heights. Overridable in a dev browser so
 *   the iOS face can be reviewed on a machine with no Mac attached.
 */
export function usePlatform() {
  const platform = Capacitor.getPlatform()
  const isNative = Capacitor.isNativePlatform()
  const isIos = platform === 'ios'
  const isAndroid = platform === 'android'

  return { platform, isNative, isIos, isAndroid }
}

/** Whether the appearance override is even offered (dev, in a browser). */
export function canPreviewPlatform(): boolean {
  return import.meta.env.DEV && !Capacitor.isNativePlatform()
}

/**
 * The platform whose LOOK to wear. Identical to the real platform everywhere
 * except a dev browser that has opted into a preview.
 */
export function appearancePlatform(): AppearancePlatform {
  if (canPreviewPlatform()) {
    try {
      const override = localStorage.getItem(PREVIEW_KEY)
      if (override === 'ios' || override === 'android') return override
    } catch {
      // Storage can throw outright (private mode, blocked site data). A preview
      // convenience must never be the thing that stops the app booting.
    }
  }
  const real = Capacitor.getPlatform()
  return isAppearancePlatform(real) ? real : 'web'
}

/**
 * Swap the previewed face. Everything platform-divergent is CSS driven off the
 * `data-platform` attribute, so re-stamping it is enough — no reload.
 */
export function setPlatformPreview(value: AppearancePlatform | null): void {
  if (!canPreviewPlatform()) return
  try {
    if (value) localStorage.setItem(PREVIEW_KEY, value)
    else localStorage.removeItem(PREVIEW_KEY)
  } catch {
    // Ignore — the attribute below still updates for this session.
  }
  document.documentElement.setAttribute('data-platform', appearancePlatform())
}
